import { KeyboardEvent, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

export type BarDatum = {
  key: string
  value: number
  /** short axis label */
  label: string
  /** tooltip / aria label */
  title: string
}

type ActivityBarChartProps = {
  data: BarDatum[]
  ariaLabel: string
  selectedKey?: string | null
  /** makes bars selectable (click / Enter / Space); clicking the selected bar clears it */
  onSelect?: (key: string | null) => void
  /** dashed reference line, e.g. members */
  referenceValue?: number | null
  referenceLabel?: string
  height?: number
  /** tailwind fill class of the bars */
  barClassName?: string
  selectedBarClassName?: string
}

const AXIS_WIDTH = 32
const LABEL_HEIGHT = 20
const TOP_PAD = 16

const useElementWidth = <T extends HTMLElement>() => {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = ref.current

    if (!el) return

    setWidth(el.clientWidth)

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  return [ref, width] as const
}

/** 0, 15, 30, 45, 60 style ticks */
export const niceTicks = (max: number, count = 4): number[] => {
  if (max <= 0) return [0, 1]

  const raw = max / count
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step =
    [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ??
    10 * magnitude
  const top = Math.ceil(max / step) * step

  return Array.from({ length: Math.round(top / step) + 1 }, (_, i) =>
    Number((i * step).toFixed(6))
  )
}

/**
 * Hand-rolled responsive SVG bar chart (no chart dependency).
 */
export const ActivityBarChart = ({
  data,
  ariaLabel,
  selectedKey,
  onSelect,
  referenceValue,
  referenceLabel = 'members',
  height = 220,
  barClassName = 'fill-secondary/60 hover:fill-secondary/80',
  selectedBarClassName = 'fill-secondary'
}: ActivityBarChartProps) => {
  const [containerRef, width] = useElementWidth<HTMLDivElement>()

  const maxValue = Math.max(referenceValue ?? 0, ...data.map((d) => d.value), 1)
  const ticks = niceTicks(maxValue)
  const top = ticks[ticks.length - 1]
  const plotWidth = Math.max(0, width - AXIS_WIDTH)
  const plotHeight = height - LABEL_HEIGHT - TOP_PAD
  const slot = data.length ? plotWidth / data.length : 0
  const barWidth = Math.max(1, Math.min(48, slot * 0.72))
  const y = (value: number) => TOP_PAD + plotHeight - (value / top) * plotHeight
  // show every n-th axis label so they don't overlap (~44px per label)
  const labelEvery = Math.max(1, Math.ceil(44 / Math.max(slot, 1)))
  const hasSelection = !!selectedKey && data.some((d) => d.key === selectedKey)

  const handleKey = (event: KeyboardEvent<SVGGElement>, key: string) => {
    if (!onSelect) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(key === selectedKey ? null : key)
    } else if (event.key === 'Escape') {
      onSelect(null)
    }
  }

  return (
    <div className="w-full text-foreground" ref={containerRef}>
      {width > 0 && (
        <svg
          aria-label={ariaLabel}
          className="block overflow-visible"
          height={height}
          role="group"
          width={width}
        >
          {/* grid + y axis */}
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                className="stroke-border/40"
                x1={AXIS_WIDTH}
                x2={width}
                y1={y(tick)}
                y2={y(tick)}
              />
              <text
                className="fill-muted-foreground/60 text-[10px] tabular-nums"
                dominantBaseline="middle"
                textAnchor="end"
                x={AXIS_WIDTH - 6}
                y={y(tick)}
              >
                {tick}
              </text>
            </g>
          ))}

          {/* bars */}
          {data.map((d, i) => {
            const x = AXIS_WIDTH + i * slot + (slot - barWidth) / 2
            const barTop = y(d.value)
            const isSelected = d.key === selectedKey
            const showLabel =
              (data.length - 1 - i) % labelEvery === 0 || isSelected

            return (
              <g
                aria-label={d.title}
                aria-pressed={onSelect ? isSelected : undefined}
                className={cn(
                  'outline-none [&:focus-visible>rect.bar]:stroke-ring [&:focus-visible>rect.bar]:stroke-2',
                  onSelect && 'cursor-pointer'
                )}
                key={d.key}
                onClick={
                  onSelect
                    ? () => onSelect(isSelected ? null : d.key)
                    : undefined
                }
                onKeyDown={(event) => handleKey(event, d.key)}
                role={onSelect ? 'button' : 'img'}
                tabIndex={onSelect ? 0 : undefined}
              >
                <title>{d.title}</title>
                {/* full-height hit area */}
                <rect
                  fill="transparent"
                  height={plotHeight + TOP_PAD}
                  width={slot}
                  x={AXIS_WIDTH + i * slot}
                  y={0}
                />
                <rect
                  className={cn(
                    'bar transition-colors',
                    isSelected
                      ? selectedBarClassName
                      : hasSelection
                      ? 'fill-secondary/30 hover:fill-secondary/50'
                      : barClassName
                  )}
                  height={Math.max(0, TOP_PAD + plotHeight - barTop)}
                  rx={Math.min(3, barWidth / 4)}
                  width={barWidth}
                  x={x}
                  y={barTop}
                />
                {isSelected && (
                  <text
                    className="fill-foreground text-[11px] font-semibold tabular-nums"
                    textAnchor="middle"
                    x={x + barWidth / 2}
                    y={barTop - 5}
                  >
                    {d.value}
                  </text>
                )}
                {showLabel && (
                  <text
                    className={cn(
                      'text-[10px] tabular-nums',
                      isSelected
                        ? 'fill-foreground font-semibold'
                        : 'fill-muted-foreground/60'
                    )}
                    textAnchor="middle"
                    x={x + barWidth / 2}
                    y={height - 4}
                  >
                    {d.label}
                  </text>
                )}
              </g>
            )
          })}

          {/* reference line */}
          {referenceValue !== null && referenceValue !== undefined && (
            <g>
              <title>{`${referenceValue} ${referenceLabel}`}</title>
              <line
                className="stroke-muted-foreground/60"
                strokeDasharray="4 3"
                x1={AXIS_WIDTH}
                x2={width}
                y1={y(referenceValue)}
                y2={y(referenceValue)}
              />
            </g>
          )}
        </svg>
      )}
    </div>
  )
}

export const ChartLegend = ({
  items
}: {
  items: { label: string; kind: 'bar' | 'dashed' }[]
}) => (
  <div className="flex items-center gap-3 text-xs text-muted-foreground/80">
    {items.map((item) => (
      <span className="inline-flex items-center gap-1.5" key={item.label}>
        {item.kind === 'bar' ? (
          <span className="h-2.5 w-2.5 rounded-sm bg-secondary" />
        ) : (
          <span className="w-3 border-t border-dashed border-muted-foreground/70" />
        )}
        {item.label}
      </span>
    ))}
  </div>
)
